import { useEffect, useMemo, useState } from "react"
import { useLanguage } from "@/lib/lang"
import { t } from "@/lib/i18n"

type Consent = {
  necessary: true
  preferences: boolean
  analytics: boolean
  marketing: boolean
  updatedAt: string
}

const STORAGE_KEY = "cookieConsent.v1"

function readConsent(): Consent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<Consent>
    if (parsed.necessary !== true) return null
    return {
      necessary: true,
      preferences: Boolean(parsed.preferences),
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      updatedAt: String(parsed.updatedAt || ""),
    }
  } catch {
    return null
  }
}

function writeConsent(consent: Consent) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent))
  } catch {
  }
}

export function CookieConsentBanner() {
  const { lang } = useLanguage()
  const [open, setOpen] = useState(false)
  const [showManage, setShowManage] = useState(false)
  const [draft, setDraft] = useState<Consent>(() => ({
    necessary: true,
    preferences: false,
    analytics: false,
    marketing: false,
    updatedAt: "",
  }))

  useEffect(() => {
    const existing = readConsent()
    if (existing) {
      setOpen(false)
      setDraft(existing)
      return
    }
    const c: Consent = { necessary: true, preferences: false, analytics: false, marketing: false, updatedAt: new Date().toISOString() }
    writeConsent(c)
    setDraft(c)
    setOpen(false)
  }, [])

  const nowConsent = useMemo(() => {
    return { ...draft, necessary: true, updatedAt: new Date().toISOString() } as Consent
  }, [draft])

  const acceptAll = () => {
    const c: Consent = { necessary: true, preferences: true, analytics: true, marketing: true, updatedAt: new Date().toISOString() }
    writeConsent(c)
    setDraft(c)
    setOpen(false)
    setShowManage(false)
  }

  const rejectNonEssential = () => {
    const c: Consent = { necessary: true, preferences: false, analytics: false, marketing: false, updatedAt: new Date().toISOString() }
    writeConsent(c)
    setDraft(c)
    setOpen(false)
    setShowManage(false)
  }

  const save = () => {
    writeConsent(nowConsent)
    setDraft(nowConsent)
    setOpen(false)
    setShowManage(false)
  }

  if (!open) return null

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <div className="mx-auto max-w-4xl rounded-2xl border border-emerald-200 bg-white/95 backdrop-blur shadow-lg p-4">
          <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
            <div className="flex-1">
              <div className="text-base font-semibold text-slate-900">{t("cookie_consent_title", lang)}</div>
              <div className="mt-1 text-sm text-slate-700">{t("cookie_consent_body", lang)}</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <button
                type="button"
                onClick={rejectNonEssential}
                className="px-3 py-2 rounded-md border border-slate-300 text-slate-800 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
              >
                {t("cookie_consent_reject", lang)}
              </button>
              <button
                type="button"
                onClick={() => setShowManage(true)}
                className="px-3 py-2 rounded-md border border-emerald-200 text-emerald-900 hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
              >
                {t("cookie_consent_manage", lang)}
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="px-3 py-2 rounded-md bg-emerald-700 text-white hover:bg-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
              >
                {t("cookie_consent_accept", lang)}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showManage && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-end sm:items-center justify-center p-3">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl border border-emerald-200 p-5">
            <div className="flex items-center justify-between gap-4">
              <div className="text-lg font-semibold text-slate-900">{t("cookie_consent_manage_title", lang)}</div>
              <button
                type="button"
                onClick={() => setShowManage(false)}
                className="px-2 py-1 rounded-md text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
                aria-label={t("cookie_consent_close", lang)}
              >
                {t("cookie_consent_close", lang)}
              </button>
            </div>

            <div className="mt-4 space-y-3 text-sm text-slate-800">
              <label className="flex items-start gap-3">
                <input type="checkbox" checked disabled className="mt-1" />
                <span>
                  <span className="font-medium text-slate-900">{t("cookie_cat_necessary", lang)}</span>
                  <span className="block text-slate-700">{t("cookie_cat_necessary_desc", lang)}</span>
                </span>
              </label>

              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={draft.preferences}
                  onChange={(e) => setDraft((d) => ({ ...d, preferences: e.target.checked }))}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium text-slate-900">{t("cookie_cat_preferences", lang)}</span>
                  <span className="block text-slate-700">{t("cookie_cat_preferences_desc", lang)}</span>
                </span>
              </label>

              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={draft.analytics}
                  onChange={(e) => setDraft((d) => ({ ...d, analytics: e.target.checked }))}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium text-slate-900">{t("cookie_cat_analytics", lang)}</span>
                  <span className="block text-slate-700">{t("cookie_cat_analytics_desc", lang)}</span>
                </span>
              </label>

              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={draft.marketing}
                  onChange={(e) => setDraft((d) => ({ ...d, marketing: e.target.checked }))}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium text-slate-900">{t("cookie_cat_marketing", lang)}</span>
                  <span className="block text-slate-700">{t("cookie_cat_marketing_desc", lang)}</span>
                </span>
              </label>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={rejectNonEssential}
                className="px-3 py-2 rounded-md border border-slate-300 text-slate-800 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
              >
                {t("cookie_consent_reject", lang)}
              </button>
              <button
                type="button"
                onClick={save}
                className="px-3 py-2 rounded-md bg-emerald-700 text-white hover:bg-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
              >
                {t("cookie_consent_save", lang)}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
